<template>
  <div class="flex flex-row items-center px-6 h-6">
    <UserAvatar
      class="mr-2 cursor-default"
      :member="member"
    />
    <div
      v-member-hover-preview="getHoverMember"
      class="flex flex-row truncate"
    >
      <span class="text-sm text-text-default overflow-hidden text-ellipsis whitespace-nowrap">
        {{ nameToBeDislayed }}
      </span>
      <span
        v-if="displayMe"
        class="flex flex-shrink-0 text-sm text-text-default"
      >
        &nbsp;{{ displayMe }}
      </span>
      <span
        v-if="displayOwner"
        class="flex flex-shrink-0 text-sm text-text-default"
      >
        &nbsp;{{ displayOwner }}
      </span>
      <span
        v-if="displayDeactivated"
        class="flex flex-shrink-0 ml-0 text-sm text-text-default"
      >
        &nbsp;{{ displayDeactivated }}
      </span>
    </div>

    <v-popover
      v-if="displaySettingsButton && memberItemSettingsDropdownItems.length > 0"
      interactive
      tag="div"
      content-tag="div"
      placement="bottom-end"
      class="flex ml-auto rounded"
      :append-to="bodyRef"
      :role="'poper'"
      :trigger="'click'"
    >
      <template #default="{ state }">
        <div
          v-if="!isGuestRole"
          :class="{ 'bg-icon-bg': state.isVisible }"
          class="group hover:bg-icon-bg h-6 w-6 flex items-center justify-center rounded cursor-pointer"
        >
          <em
            class="custom-icons bg-icon-default group-hover:bg-icon-hover"
            :style="getCustomIconStyle(iconPath('more-horizontal'))"
          />
        </div>
      </template>
      <template #content="{ state, hide }">
        <ListDropdown
          v-if="state.isVisible"
          :items="memberItemSettingsDropdownItems"
          class="relative"
          @click="handleItemSettingsDropdownClick($event, hide)"
          @closeEscape="hide"
        />
      </template>
    </v-popover>
  </div>
</template>

<script>
import UserAvatar from '@/components/widgets/UserAvatar';
import ListDropdown from '@/components/widgets/ListDropdown';
import getMemberLabel from '@/utilities/getMemberLabel';
import EventBus from '@/utilities/eventBus';
import customIconsMixin from '@/mixins/customIconsMixin';
import { TeamMemberRole } from '@superthread-com/api';

export default {
  name: 'InfoSidebarMemberItem',
  components: {
    UserAvatar,
    ListDropdown,
  },
  mixins: [customIconsMixin],
  props: {
    member: {
      type: Object,
      required: true,
    },
    project: {
      type: Object,
      required: true,
    },
    displaySettingsButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['removeMember'],
  setup() {
    return {
      bodyRef: document.body,
    };
  },
  computed: {
    currentUserId() {
      return this.$store.getters.getUserId;
    },
    currentRole() {
      return this.$store.getters.getCurrentRole;
    },
    isCurrentUserWorkspaceAdmin() {
      return this.currentRole === TeamMemberRole.Admin || this.currentRole === TeamMemberRole.Owner;
    },
    isCurrentUserSpaceOwner() {
      return this.project.user_id === this.currentUserId;
    },
    isCurrentUserSpaceMember() {
      return this.project.members?.some((member) => member.user_id === this.currentUserId) || false;
    },
    isMemberCurrentUser() {
      return this.member.id === this.currentUserId;
    },
    isMemberSpaceOwner() {
      return this.member.id === this.project.user_id;
    },
    nameToBeDislayed() {
      const label = getMemberLabel(this.member, true);
      if (this.isGuestRole && !label) return this.translate('hiddenMember');
      return label;
    },
    displayMe() {
      return this.member.id === this.currentUserId
        ? ` (${this.translate('me').toLowerCase()})`
        : '';
    },
    displayOwner() {
      return this.isMemberSpaceOwner ? ` (${this.translate('owner').toLowerCase()})` : '';
    },
    displayDeactivated() {
      const inactiveMember = this.$store.getters.getInactiveTeamMembers[this.member.id] || {};
      const isDeactivated = Object.keys(inactiveMember).length > 0;
      const guestRole =
        this.member.role === 'guest' ? ` (${this.translate('guest').toLowerCase()})` : '';
      return isDeactivated ? ` (${this.translate('deactivated')})` : guestRole;
    },
    memberItemSettingsDropdownItems() {
      const items = [];

      // only the owner can remove others from private spaces
      if (
        !this.isMemberCurrentUser &&
        !this.isMemberSpaceOwner &&
        (!this.project.is_private ||
          this.isCurrentUserSpaceOwner ||
          (this.isCurrentUserWorkspaceAdmin && this.isCurrentUserSpaceMember))
      ) {
        items.push({
          id: 'removeFromSpace',
          label: this.translate('removeFromSpace'),
        });
      }

      // members can leave the space
      // except the owner since they need to transfer ownership first
      if (this.isMemberCurrentUser && !this.isMemberSpaceOwner) {
        items.push({
          id: 'leaveSpace',
          label: this.translate('leaveSpace'),
        });
      }

      return items;
    },
    getHoverMember() {
      if (this.isGuestRole && !Object.keys(this.member).length) {
        return { id: this.member.id };
      }
      return this.member;
    },
  },
  methods: {
    handleItemSettingsDropdownClick(item, hideFn) {
      if (typeof hideFn === 'function') hideFn();
      switch (item.id) {
        case 'removeFromSpace':
          this.$emit('removeMember', this.member.id);
          break;
        case 'leaveSpace':
          if (this.isCurrentUserSpaceOwner) {
            EventBus.$emit('mustTransferSpaceOwner', this.project.id);
          } else {
            this.$emit('removeMember', this.member.id);
          }
      }
    },
  },
};
</script>
